import React from 'react'

import { PlatformEnum, ImportTypeEnum } from '../pages/Settings/types'
import { LoadingStatusEnum } from '../store/types'

import type {
  TraderProfitabilityTag,
  TraderProfitabilityUnknownReason,
} from './traderProfitability'

export enum UserRolesEnum {
  USER = 'USER',
  ADMIN = 'ADMIN',
}

export type UserRole = UserRolesEnum

export enum AccountPricingModelEnum {
  rawNoSpread = 'RAW_NO_SPREAD',
  spreadOnly = 'SPREAD_ONLY',
  hybrid = 'HYBRID',
  unknown = 'UNKNOWN',
}

export enum AccountBreachStatusEnum {
  active = 'active',
  suspected = 'suspected',
  assumed = 'assumed',
  confirmed = 'confirmed',
}

export enum AccountBreachReasonEnum {
  syncRepeatedFailures = 'sync_repeated_failures',
  syncInvalidCredentials = 'sync_invalid_credentials',
  inactivity14d = 'inactivity_14d',
  equityZero = 'equity_zero',
  equityBelowThreshold = 'equity_below_threshold',
  tradeDisabled = 'trade_disabled',
  manual = 'manual',
}

export type UserPlatform = 'web' | 'ios' | 'android'

export type User = {
  id: string
  firstName: string
  lastName: string
  userName: string
  country: string
  city: string
  email: string
  phone: string
  isEmailVerified: boolean
  isPhoneConfirmed: boolean
  isVerified: boolean
  googleAuth: boolean
  appleAuth: boolean
  isQuestionnaireCompleted: boolean
  role: UserRolesEnum
  registrationPlatform?: UserPlatform
  usedPlatforms?: UserPlatform[]
  lastActivePlatform?: UserPlatform
}

export type UserAccount = {
  id: number
  name: string
  platform: PlatformEnum
  balance: number
  currency: string
  login?: string
  password?: string
  server?: string
  manualBrokerName?: string
  excludedFromTop: string
  importType: ImportTypeEnum
  brokerId?: number
  isGeneratedDemo: boolean
  isDemo: boolean
  isProp: boolean
  isCfd: boolean
  pricingModel?: AccountPricingModelEnum
  pricingModelDetectedAt?: string
  credentialsValid?: boolean
  breachStatus?: AccountBreachStatusEnum
  breachReason?: AccountBreachReasonEnum | null
  breachDetectedAt?: string | null
  breachEffectiveAt?: string | null
  breachedInProfit?: boolean | null
  breachPnl?: number | null
  lastClosedTradeAt?: string | null
  lastWithdrawalAt?: string | null
  withdrawalCount?: number | null
  totalWithdrawals?: number | null
  lastSyncSucceededAt?: string | null
  lastSyncFailedAt?: string | null
  lastSyncFailureReason?: string | null
  consecutiveSyncFailures?: number
  profitabilityTag?: TraderProfitabilityTag
  profitabilityUnknownReason?: TraderProfitabilityUnknownReason | null
  profitabilityNetPnl30dUsd?: number | null
  profitabilityNativeNetPnl?: number | null
  profitabilityNativeCurrency?: string | null
  profitabilityTradeCount30d?: number
  profitabilityWinRate30d?: number | null
  profitabilityProfitFactor30d?: number | null
  profitabilityWindowStartedAt?: string | null
  profitabilityCalculatedAt?: string | null
}

export type UserAccountsInitialStateType = {
  userAccounts: UserAccount[]
  currentAccount: UserAccount | null
  period: [string | null, string | null]
  lastAccountUpdate?: string
  isRunningAutoUpload: boolean
  isHydrated: boolean
  loading: LoadingStatusEnum
  accountSyncDateLoading: LoadingStatusEnum
}

export type UserContextType = {
  isAuthentificated: boolean
  currentUser: User | null
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>
  setIsAccountConnected: React.Dispatch<React.SetStateAction<boolean>>
  setSkipAccountConnection: React.Dispatch<React.SetStateAction<boolean>>
  personalDataFormId: string
  isOnboadringCompleted: boolean
  isAccountConnected: boolean
  onLogout: () => void
}

export interface AuthState {
  token: string | null
  refreshToken: string | null
  currentUser: User | null
}

export interface ResetPasswordData {
  email: string
  code: string
  newPassword: string
}

export type CheckUserNameData = {
  isNameAvailable: string
  message: string
}

export type LastAccountDataUpdate = {
  date?: string
}

export enum AutoUploadStatusEnum {
  pending = 'pending',
  success = 'success',
  error = 'error',
  unregistered = 'unregistered',
}

export enum AutoUploadTriggerEnum {
  true = 'true',
  false = 'false',
}
